import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';


import useCherryserviceAPI from '../../api/cherryservice/v2';
import { ProductType, Subscription as SubscriptionApiObj, SubscriptionStatus as SubscriptionAPIStatus } from '../../api/cherryservice/v2/types';
import { InteractiveModal } from '../../components/modals';
import {
  GridCard,
  GridCardColumn,
  GridCardButtonColumn,
  InnerListCard,
  ListCardItem,
  ListCardButton,
  ListCardNavButton,
  ElementCard,
  SmallMetricCard
} from '../../components/cards';
import { FlatButton } from '../../components/buttons';
import { useDebugLogger, useMounted, useTimer } from '../../commons/hooks';

const GRAPHANA_MODAL = "GRAPHANA";

type Project = {
  id: number,
  name: string,
  date: Date,
  subscriptions: Subscription[],
  monitor?: MonitorSubscription,
  client?: ClientSubscription
}

enum SubscriptionStatus {
  INSTALLING = "Installing",
  ACTIVE = "Active",
  ERROR = "Error",
  INACTIVE = "Inactive",
  UNTRACKED = "Untracked" //Used only in case the response from the server is not handled
}


type Subscription = {
  id: number
  name: string
  servers: number
  status: SubscriptionStatus
}

type MonitorSubscription = {
  id: number,
  url: string,
  status: SubscriptionStatus
}

interface ClientSubscription {
  id: number,
  status: SubscriptionStatus
}

const subscriptionStatusesMap = new Map<SubscriptionAPIStatus, SubscriptionStatus>([
  [SubscriptionAPIStatus.ACTIVE, SubscriptionStatus.ACTIVE],
  [SubscriptionAPIStatus.ERROR, SubscriptionStatus.ERROR],
  [SubscriptionAPIStatus.INACTIVE, SubscriptionStatus.INACTIVE],
  [SubscriptionAPIStatus.BOOTING, SubscriptionStatus.INSTALLING],
  [SubscriptionAPIStatus.INSTANTIATING, SubscriptionStatus.INSTALLING],
  [SubscriptionAPIStatus.INSTALLING, SubscriptionStatus.INSTALLING],
]);

const SubscriptionCard: React.FC<{ projectID: number, subscriptions: number, disabled?: boolean }> = ({ subscriptions, projectID, ...props }) => {
  const location = useLocation();
  const history = useHistory();

  function handleClick() {
    history.push(`${location.pathname.toString() + '/subscription/new'}`, {
      projectID: projectID
    })
  }

  return (
    <ElementCard
      iconBgColor="bg-white"
      bgColor="bg-gradient-to-tr from-safety-orange-blaze via-red-crayola to-dark-magenta"
      icon={<svg style={{ marginLeft: '-3.5px' }} height="35pt" viewBox="0 -54 512.001 512" width="35pt" xmlns="http://www.w3.org/2000/svg"><path fill="#E94057" d="m417.097656 100.953125c-3.117187 0-6.199218.152344-9.242187.445313-1.441407-56.246094-47.484375-101.398438-104.070313-101.398438-44.566406 0-82.585937 28.011719-97.4375 67.390625-21.800781-18.015625-49.761718-28.835937-80.25-28.835937-69.644531 0-126.097656 56.460937-126.097656 126.113281 0 69.652343 56.453125 126.113281 126.097656 126.113281h124.191406c-.003906-.433594-.015624-.867188-.015624-1.300781 0-38.675781 15.0625-75.035157 42.410156-102.382813s63.707031-42.410156 102.382812-42.410156c38.675782 0 75.035156 15.0625 102.382813 42.410156 5.195312 5.195313 9.945312 10.714844 14.234375 16.515625.207031-2.554687.316406-5.136719.316406-7.746093 0-52.421876-42.488281-94.914063-94.902344-94.914063zm0 0" /><path fill="#8A2387" d="m395.066406 174.878906c-63.292968 0-114.601562 51.308594-114.601562 114.601563s51.308594 114.601562 114.601562 114.601562c63.292969 0 114.597656-51.308593 114.597656-114.601562s-51.304687-114.601563-114.597656-114.601563zm43.902344 176.546875c-12.578125 8.675781-27.328125 13.261719-42.65625 13.261719-29.75 0-55.519531-17.367188-67.707031-42.492188-2.058594 1.953126-3.203125 2.339844-5.144531 2.988282-1.589844.53125-3.203126.785156-4.792969.785156-6.3125 0-12.199219-3.992188-14.3125-10.308594-1.894531-5.664062-.246094-11.65625 3.746093-15.578125 1.9375-2.226562 9.617188-11.691406 16.328126-20.050781 4.191406-5.152344 10.144531-7.828125 17.953124-4.324219l23.832032 10.695313c7.609375 3.414062 11.007812 12.347656 7.59375 19.953125-2.597656 5.789062-8.394532 9.132812-14.371094 8.898437 8.148438 11.621094 21.636719 19.242188 36.875 19.242188 9.179688 0 18-2.742188 25.515625-7.921875 6.863281-4.734375 16.265625-3.003907 21 3.855469 4.734375 6.863281 3.007813 16.265624-3.859375 20.996093zm43.511719-70.804687-14.152344 18.144531c-4.738281 5.78125-9.957031 6.59375-15.660156 5.332031l-26.027344-6.691406c-8.074219-2.078125-12.9375-10.308594-10.859375-18.382812 2.078125-8.074219 10.304688-12.9375 18.378906-10.859376l2.066406.53125c-7.523437-14.386718-22.585937-24.230468-39.914062-24.230468-6.402344 0-12.582031 1.308594-18.363281 3.898437-7.613281 3.40625-16.539063-.003906-19.945313-7.613281s.003906-16.542969 7.613282-19.945312c9.6875-4.335938 20.015624-6.535157 30.699218-6.535157 30.066406 0 56.054688 17.742188 68.082032 43.300781 4.9375-2.214843 10.902343-1.699218 15.46875 1.863282 6.574218 5.128906 7.742187 14.617187 2.613281 21.1875zm0 0" /></svg>}
      fillHeight={true}
    >
      <div>
        <h2 className="text-xl font-header text-gray-50 font-bold">Subscriptions</h2>
        <h1 className="text-5xl font-header text-white font-bold pt-2">{subscriptions}</h1>
        <FlatButton disabled={props.disabled} onClick={handleClick} className={`absolute px-2 py-1 right-2 bottom-2 ${props.disabled ? '' : 'hover:bg-white '}hover:bg-opacity-30 rounded text-white disabled:text-gray-200`}>
          {
            props.disabled
              ? <p className="text-sm font-bold">LIMIT REACHED</p>
              : <p className="text-sm font-bold">ADD SUBSCRIPTION</p>
          }
        </FlatButton>
      </div>
    </ElementCard>
  );
}


const ProjectPage: React.FC<{}> = (props) => {
  const history = useHistory();
  const location = useLocation();
  const projectID = parseInt(location.pathname.split('/project/')[1]);
  const [newInstanceLoading, setNewInstanceLoading] = useState(false);
  const [modalIsActive, setModalIsActive] = useState(false);
  const [isClientReqDispatching, setIsClientReqDispatching] = useState(false);
  const [isDashboardReqDispatching, setIsDashboardReqDispatching] = useState(false);
  const [availableSubscriptions, setAvailableSubscriptions] = useState<number | null>(null);
  const mounted = useMounted();
  const logger = useDebugLogger();
  const timer = useTimer();
  const cherryserviceAPI = useCherryserviceAPI();

  const [project, setProject] = useState<Project | null>(null);

  async function handleCreateMonitor() {
    // If project and cherryservice endpoint initialized
    if (cherryserviceAPI&&project) {
      setNewInstanceLoading(true);
      setIsDashboardReqDispatching(true);
      try {
        const products = await cherryserviceAPI.getProducts();
        const monitor = products.data.find(prod => prod.type === ProductType.MONITOR)
        if (monitor !== undefined) {
          cherryserviceAPI.newMonitorSubscription(monitor.id, project.id, project.subscriptions.map(sub => sub.id))
            .then(() => {
              setNewInstanceLoading(false);
            }).catch((err) => {
              if (mounted) setNewInstanceLoading(false);
              //TODO: Handle errors
            })
        }
      } catch (error: any) {
        if (error.response){
          logger.log(error)
        }
      }
    }
  }

  const manageProjectSubscriptionsResponse = useCallback((projectData: Project, responseData: SubscriptionApiObj[]) => {
    if(cherryserviceAPI){
      //Map all products to understand their type
    const productTypesMap = new Map<number, ProductType>();
    let prodPromises: Promise<void>[] = [];
    responseData.forEach(sub => {
      if (productTypesMap.get(sub.product) === undefined) {
        productTypesMap.set(sub.product, ProductType.MONITOR) //Set empty value to avoid useless remote requests for the same prod
        prodPromises.push(cherryserviceAPI.getProduct(sub.product)
          .then(prodRes => {
            productTypesMap.set(prodRes.data.id, prodRes.data.type)
          })
        )
      }
    })
    //After retrieving products map subscriptions
    Promise.all(prodPromises).then(() => {
      //map response structure to local structure
      //ADD TO SUBS ONLY CHERRY TABLE AND NOT MONITOR
      responseData.filter(sub => productTypesMap.get(sub.product) === ProductType.CHERRY_TABLE).forEach(sub => { //FIXME: get products and filters all products of type monitor
        projectData.subscriptions.push({
          id: sub.id,
          name: sub.name,
          servers: sub.servers.length,
          status: subscriptionStatusesMap.get(sub.status) ?? SubscriptionStatus.UNTRACKED
        })
      })
      //EXTRACT THE MONITOR
      const monitor = responseData.find(sub => productTypesMap.get(sub.product) === ProductType.MONITOR);
      if (monitor) {
        setIsDashboardReqDispatching(false);
        projectData.monitor = {
          id: monitor.id,
          url: monitor.servers.length > 0 ? `http://${monitor.servers[0].ip_address}:3000` : '',
          status: subscriptionStatusesMap.get(monitor.status) ?? SubscriptionStatus.UNTRACKED
        }
      }
      //EXTRACT THE CLIENT
      const client = responseData.find(sub => productTypesMap.get(sub.product) === ProductType.CLIENT);
      if (client) {
        setIsClientReqDispatching(false);
        projectData.client = {
          id: client.id,
          status: subscriptionStatusesMap.get(client.status)??SubscriptionStatus.UNTRACKED,
        }
      }

      //then update project state
      if (mounted) {
        setProject(projectData);
      }
    }).catch(err => err.response && logger.log(err.response))
    }
  }, [mounted, cherryserviceAPI, logger])

  function enableSubmit(projectName: string) {
    if (projectName === project?.name) {
      return null;
    }
    return '';
  }

  function handleDelete() {
    cherryserviceAPI?.deleteProject(projectID)
      .then(res => {
        history.goBack();
      }).catch(err => {
        //FIXME: manage errors
        if(err.response){
          logger.log(err)
        }
      });

  }

  async function handleCreateClient() {
    if (cherryserviceAPI&&project) {
      setNewInstanceLoading(true);
      setIsClientReqDispatching(true);
      try {
        const products = await cherryserviceAPI.getProducts();
        const client = products.data.find(prod => prod.type === ProductType.CLIENT)
        if (client !== undefined) {
          cherryserviceAPI.newClientSubscription(client.id, project.id, project.subscriptions.map(sub => sub.id))
            .then(() => {
              setNewInstanceLoading(false);
            }).catch((err) => {
              if (mounted){
                setNewInstanceLoading(false);
                logger.log(err)
              } 
            })
        } else {
          logger.log('Client product not found in the back-end!')
        }
      } catch (error) {
        logger.log(error)
      }
    }
  }

  useEffect(() => {
    if (mounted) {
      cherryserviceAPI?.getMyLimits()
        .then(res => {
          if (mounted) {
            setAvailableSubscriptions(res.data.number_of_available_subscriptions_per_project[projectID] ?? 0)
          }
        }).catch(err => err.response && logger.log(err.response))
    }
  }, [projectID, mounted, cherryserviceAPI, logger])

  //Reload subscriptions if they are not active
  useEffect(() => {
    function retrieveSubscriptions() {
      if (project) {
        cherryserviceAPI?.getSubscriptionsByProject(project.id).then(res => {
          const projectData: Project = { ...project } //Get actual project copy
          projectData.subscriptions = [] //RESET subscriptions
          manageProjectSubscriptionsResponse(projectData, res.data)
        }).catch(err => err.response && logger.log(err.response))
      }
    }

    if (project) {
      const thereIsASettingUpSubscription = project.subscriptions.filter(sub => sub.status === SubscriptionStatus.INSTALLING).length > 0
      const monitorIsSettingUp = project.monitor && project.monitor.status === SubscriptionStatus.INSTALLING
      const clientIsSettingUp = project.client && project.client.status === SubscriptionStatus.INSTALLING
      if (thereIsASettingUpSubscription || monitorIsSettingUp || clientIsSettingUp) {
        timer.setTimer(() => retrieveSubscriptions(), 2000);
      } else {
        timer.clearTimer()
      }
    }
  }, [project, timer, manageProjectSubscriptionsResponse,cherryserviceAPI, logger])

  useEffect(() => {
    if (!newInstanceLoading && mounted && cherryserviceAPI) {
      //get project
      cherryserviceAPI.getProject(projectID).then(res => {
        let project = res.data;

        //map response structure to local structure
        let projectData: Project = {
          id: project.id,
          name: project.name,
          date: new Date(res.data.date_created),
          subscriptions: [],
        }

        //retrieve project's subscription
        cherryserviceAPI.getSubscriptionsByProject(project.id).then(res2 => {
          manageProjectSubscriptionsResponse(projectData, res2.data)
        }).catch(err => err.response && logger.log(err.response));
      }).catch(err => err.response && logger.log(err.response)); //TODO: log errors
    }
  }, [projectID, newInstanceLoading, mounted, manageProjectSubscriptionsResponse, cherryserviceAPI, logger])

  if (project === null || availableSubscriptions === null) {
    return (
      <div className="w-full h-full pt-8 px-8 flex flex-col items-center justify-center">
        <svg className="animate-spin h-10 w-10 text-cherry-red-400" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }
  return (
    <div className={`w-full h-full ${modalIsActive && 'overflow-hidden'}`}>
      <InteractiveModal
        windowClassName={`absolute w-full h-full z-40`}
        title="Delete Project"
        inputLabel="Insert project's name to delete"
        mainButtonLabel="Delete"
        active={modalIsActive}
        enableSubmitCallback={(input: string) => enableSubmit(input)}
        backCallback={() => setModalIsActive(false)}
        submitCallback={() => handleDelete()}
      />
      <div className="p-8">
        <div className="flex flex-row items-center space-x-4">
          <i onClick={() => history.goBack()} className="fas fa-chevron-left text-5xl text-gunmetal-500 cursor-pointer hover:text-gunmetal-300"></i>
          <h1 className="text-6xl font-bold font-header text-gunmetal-500">{project.name}</h1>
        </div>
        <div className="mt-8 grid grid-cols-9 gap-8" style={{ gridAutoRows: 'min-content' }}>
          <div className="col-span-3 h-100">
            <InnerListCard
              header="Project info"
              autoSize={true}
            >
              <ListCardItem
                title="Project ID"
                value={`PR-${project.id}`}
                icon={<svg className="-ml-1 -mr-1.5" width="21.5pt" height="18pt" viewBox="0 0 18 18" preserveAspectRatio="xMidYMid meet" focusable="false"><path d="M10.557 11.99l-1.71-2.966 1.71-3.015h3.42l1.71 3.01-1.71 2.964h-3.42zM4.023 16l-1.71-2.966 1.71-3.015h3.42l1.71 3.01L7.443 16h-3.42zm0-8.016l-1.71-2.966 1.71-3.015h3.42l1.71 3.015-1.71 2.966h-3.42z" fill="#74797E" ></path></svg>}
              />
              <ListCardItem
                title="Creation date"
                value={project.date.toLocaleDateString()}
                icon={<i className="fas fa-calendar-alt text-lg text-gunmetal-200"></i>}
              />
              {
                project.monitor
                  ? (
                    <ListCardNavButton
                      key="Go button"
                      title="Dashboard"
                      value="Open"
                      disabled={project.monitor.status === SubscriptionStatus.INSTALLING}
                      loading={project.monitor.status === SubscriptionStatus.INSTALLING}
                      infoModal={{
                        id: GRAPHANA_MODAL,
                        title: "Default credentials",
                        content: "The first time Graphana opens the default credentials are \"admin\" for the username and \"myadminpassword\" for the password. After the first login Graphana will ask you to change the password"
                      }}
                      href={project.monitor.url}
                      icon={<svg height="14pt" viewBox="0 0 512 512" width="14pt" fill="#74797e" xmlns="http://www.w3.org/2000/svg"><path d="m197.332031 0h-160c-20.585937 0-37.332031 16.746094-37.332031 37.332031v96c0 20.589844 16.746094 37.335938 37.332031 37.335938h160c20.589844 0 37.335938-16.746094 37.335938-37.335938v-96c0-20.585937-16.746094-37.332031-37.335938-37.332031zm0 0" /><path d="m197.332031 213.332031h-160c-20.585937 0-37.332031 16.746094-37.332031 37.335938v224c0 20.585937 16.746094 37.332031 37.332031 37.332031h160c20.589844 0 37.335938-16.746094 37.335938-37.332031v-224c0-20.589844-16.746094-37.335938-37.335938-37.335938zm0 0" /><path d="m474.667969 341.332031h-160c-20.589844 0-37.335938 16.746094-37.335938 37.335938v96c0 20.585937 16.746094 37.332031 37.335938 37.332031h160c20.585937 0 37.332031-16.746094 37.332031-37.332031v-96c0-20.589844-16.746094-37.335938-37.332031-37.335938zm0 0" /><path d="m474.667969 0h-160c-20.589844 0-37.335938 16.746094-37.335938 37.332031v224c0 20.589844 16.746094 37.335938 37.335938 37.335938h160c20.585937 0 37.332031-16.746094 37.332031-37.335938v-224c0-20.585937-16.746094-37.332031-37.332031-37.332031zm0 0" /></svg>}
                    />
                  ) : (
                    <ListCardButton
                      key="Create button"
                      title="Dashboard"
                      value="Create"
                      loading={isDashboardReqDispatching}
                      disabled={project.subscriptions.length === 0 || isDashboardReqDispatching || project.subscriptions.filter(sub => sub.status === SubscriptionStatus.INSTALLING).length > 0}
                      callback={() => handleCreateMonitor()}
                      icon={<svg height="14pt" viewBox="0 0 512 512" width="14pt" fill="#74797e" xmlns="http://www.w3.org/2000/svg"><path d="m197.332031 0h-160c-20.585937 0-37.332031 16.746094-37.332031 37.332031v96c0 20.589844 16.746094 37.335938 37.332031 37.335938h160c20.589844 0 37.335938-16.746094 37.335938-37.335938v-96c0-20.585937-16.746094-37.332031-37.335938-37.332031zm0 0" /><path d="m197.332031 213.332031h-160c-20.585937 0-37.332031 16.746094-37.332031 37.335938v224c0 20.585937 16.746094 37.332031 37.332031 37.332031h160c20.589844 0 37.335938-16.746094 37.335938-37.332031v-224c0-20.589844-16.746094-37.335938-37.335938-37.335938zm0 0" /><path d="m474.667969 341.332031h-160c-20.589844 0-37.335938 16.746094-37.335938 37.335938v96c0 20.585937 16.746094 37.332031 37.335938 37.332031h160c20.585937 0 37.332031-16.746094 37.332031-37.332031v-96c0-20.589844-16.746094-37.335938-37.332031-37.335938zm0 0" /><path d="m474.667969 0h-160c-20.589844 0-37.335938 16.746094-37.335938 37.332031v224c0 20.589844 16.746094 37.335938 37.335938 37.335938h160c20.585937 0 37.332031-16.746094 37.332031-37.335938v-224c0-20.585937-16.746094-37.332031-37.332031-37.332031zm0 0" /></svg>}
                    />
                  )
              }
              {
                project.client
                  ? (
                    <ListCardNavButton
                      key="Client"
                      title="Client"
                      value="Open"
                      disabled={project.client.status === SubscriptionStatus.INSTALLING}
                      loading={project.client.status === SubscriptionStatus.INSTALLING}
                      href={`/console/projects/project/${project.id}/subscription/${project.client.id}/client`}
                      icon={<i className="fas fa-server text-lg text-gunmetal-200"></i>}
                    />
                  ) : (
                    <ListCardButton
                      key="Client"
                      title="Client"
                      value="Create"
                      loading={isClientReqDispatching}
                      disabled={project.subscriptions.length === 0 || isClientReqDispatching || project.subscriptions.filter(sub => sub.status === SubscriptionStatus.INSTALLING).length > 0}
                      callback={() => handleCreateClient()}
                      icon={<i className="fas fa-server text-lg text-gunmetal-200"></i>}
                    />
                  )
              }
              <ListCardButton
                key="Delete button"
                title="Delete project"
                value="Delete"
                disabled={
                  isClientReqDispatching ||
                  isDashboardReqDispatching ||
                  project.subscriptions.filter(sub=> sub.status === SubscriptionStatus.INSTALLING).length>0 ||
                  project.monitor?.status === SubscriptionStatus.INSTALLING ||
                  project.client?.status === SubscriptionStatus.INSTALLING
                }
                callback={() => setModalIsActive(true)}
                icon={<i className="fas fa-window-close text-lg text-gunmetal-200"></i>}
              />
            </InnerListCard>
          </div>
          <div className="col-span-6 flex flex-col space-y-8">
            <div className="flex flex-row space-x-8">
              <div className="w-1/2">
                <SubscriptionCard
                  subscriptions={project.subscriptions.length}
                  projectID={project.id}
                  disabled={availableSubscriptions === 0}
                />
              </div>
              <div className="w-1/2">
                <SmallMetricCard
                  title="Next bill"
                  value="$0"
                  icon={<i className="fas fa-credit-card absolute text-white text-4xl"></i>}
                  gradient="from-blue-500 to-spring-green"
                  comment="Due date: -"
                />
              </div>
            </div>
            <div >
              <GridCard
                header="Subscriptions"
                data={
                  project.subscriptions.map(sub => {
                    return {
                      subscription: sub.name,
                      servers: sub.servers,
                      link: `/console/projects/project/${project.id}/subscription/${sub.id}`
                    }
                  })
                }
                cols={5}
              >
                <GridCardColumn
                  label="Subscription"
                  key="sub"
                  colSpan={2}
                  id={true}
                />
                <GridCardColumn
                  label="Servers"
                  key="server"
                  colSpan={2}
                />
                <GridCardButtonColumn
                  label="Link"
                  key="link"
                  colSpan={1}
                />
              </GridCard>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default ProjectPage;

/* {<BlankCard>
           <div className="flex flex-col justify-between h-full">
             <h2 className="text-xl font-header text-gunmetal-300 font-bold mb-2">Storage usage</h2>
             <div className="pr-2">
               <Line
                 data={projectChart.data}
                 options={projectChart.options}
                 height={230}
               />
             </div>
             <div className="flex flex-row items-center justify-end">
               <p className="py-1 px-2 hover:bg-cherry-red-75 text-cherry-red-400 duration-100 ease-in-out transition-colors transform -mb-1 cursor-pointer rounded-lg">Open dashboard</p>
             </div>
           </div>
         </BlankCard>} */


/*<BlankCard>
            <div className="flex flex-col justify-between h-full">
              <div className="flex flex-row justify-between items-center w-full mb-2">
                <h2 className="text-xl font-header text-gunmetal-300 font-bold ">Used storage</h2>
                <p className="px-2 py-1 bg-cherry-red-400 text-sm font-bold text-white rounded-lg">300GB</p>
              </div>
              <div className="pr-2">
                <Line
                  data={projectChart.data}
                  options={projectChart.options}
                  height={230}
                />
              </div>
              <div className="flex flex-row items-center justify-end">
                <p className="py-1 px-2 hover:bg-cherry-red-75 text-cherry-red-400 duration-100 ease-in-out transition-colors transform -mb-1 cursor-pointer rounded-lg">Open dashboard</p>
              </div>
            </div>
          </BlankCard>*/